// The privacy policy and terms, in plain words. Both are reachable signed
// out, from the sign-in page and from the Settings popup.

import type { JSX } from "react";
import { linkHandler } from "../lib/nav";
import { ownRoomPath } from "../lib/storage";
import { BackIcon } from "../components/icons";

const CONTACT = "adammirmina@gmail.com";

function Privacy(): JSX.Element {
  return (
    <div className="prose">
      <p className="muted">Last updated October 18, 2026</p>
      <p>
        This is a video calling service run by Adam Mirmina. This page says what it keeps about you, where it lives, and how
        to get rid of it.
      </p>

      <h2>If you join a call without an account</h2>
      <p>
        You give a name, which the other people in the call see. Your browser keeps that name and a random device id so it can
        fill the name in next time and reconnect you if the page reloads. The host&apos;s call history records the name you used
        and when you joined and left.
      </p>

      <h2>If you have an account</h2>
      <ul>
        <li>Your name, email address, the address of your room link, and a password stored as a one-way hash.</li>
        <li>Your settings.</li>
        <li>A record of each call in your rooms: when it started and ended and who joined, by the names they gave.</li>
        <li>If you use the phone app, a token that lets the app ring when someone calls you.</li>
      </ul>

      <h2>Audio and video</h2>
      <p>
        Calls travel through Cloudflare&apos;s real-time network between the people in the call. The picture and sound are not
        kept unless the host records.
      </p>
      <p>
        To keep calls working, the service logs how each connection went, such as when someone&apos;s sound or picture dropped
        and came back. If you answer the trivia while you wait, the questions you saw and your answers are logged with the name
        you gave.
      </p>

      <h2>Recordings</h2>
      <p>
        A call is recorded only when the host presses Record or has turned on recording for every call. Everyone in the call sees
        that it is recording. The recording is stored in Cloudflare R2 and transcribed by Cloudflare Workers AI, and Google&apos;s
        Gemini writes notes from the transcript. The host can open the recording, the transcript, and the notes. If the host has
        set an address for finished recordings, the recording and transcript are sent there, and what happens to them afterward
        is up to the host.
      </p>
      <p>
        While a call records, the host can turn on a live assistant, which follows what is said to give the host notes during the
        call. Only the host sees it. What it hears is not saved, apart from a short summary in the call&apos;s record.
      </p>

      <h2>Who can see it</h2>
      <ul>
        <li>The people in the call see and hear you while it happens.</li>
        <li>The host can see the call history for their rooms and open the call&apos;s recordings, transcripts, and notes.</li>
        <li>
          Adam runs this service and can see what is stored on it, recordings and transcripts included. He looks only to keep it
          working, to answer something you send, or to review a report.
        </li>
      </ul>

      <h2>What is not collected</h2>
      <p>No ads, no analytics, no tracking across sites, and nothing is sold or shared with anyone for their own use.</p>

      <h2>Where it lives</h2>
      <p>
        Account and call records are in a database on a server run for this service. Recordings are in Cloudflare R2. The web app
        and its server run on Cloudflare.
      </p>

      <h2>Deleting it</h2>
      <p>
        To delete your account, your room link, your call history, and every recording and transcript, or to have your name taken
        out of someone else&apos;s call history or recording, email{" "}
        <a className="textlink" href={`mailto:${CONTACT}`}>
          {CONTACT}
        </a>
        .
      </p>

      <h2>Contact</h2>
      <p>
        <a className="textlink" href={`mailto:${CONTACT}`}>
          {CONTACT}
        </a>
      </p>
    </div>
  );
}

function Terms(): JSX.Element {
  return (
    <div className="prose">
      <p className="muted">Last updated September 27, 2026</p>
      <p>These terms cover using this video calling service, run by Adam Mirmina. Using it means you agree to them.</p>

      <h2>Your account</h2>
      <p>
        Keep your password to yourself. You are responsible for what happens in rooms on your link. Your room link is yours for as
        long as you have the account.
      </p>

      <h2>Recording</h2>
      <p>
        If you record a call, you are responsible for having the consent the law where you and the other people are requires. Every
        person in the call sees that it is recording, but some places require that everyone agree first.
      </p>

      <h2>Fair use</h2>
      <p>
        Do not use the service to break the law, harass anyone, or send material you have no right to share. Accounts used that way
        can be removed.
      </p>

      <h2>Limits</h2>
      <p>
        The service is free and has a monthly limit on video. When the limit is close, new calls stop until the next month; a call
        already going finishes. Features can change.
      </p>

      <h2>No warranty</h2>
      <p>
        The service is provided as it is, without any warranty. To the extent the law allows, Adam Mirmina is not liable for
        losses from using it, including a dropped call or a recording that did not save.
      </p>

      <h2>Changes and contact</h2>
      <p>
        These terms can change, and the date above says when they last did. These terms are governed by the laws of New Jersey.
        Questions go to{" "}
        <a className="textlink" href={`mailto:${CONTACT}`}>
          {CONTACT}
        </a>
        .
      </p>
    </div>
  );
}

export function LegalScreen({ page }: { readonly page: "privacy" | "terms" }): JSX.Element {
  const back = ownRoomPath();
  return (
    <div className="page">
      <div className="page__head">
        <a className="btn btn--ghost btn--icon" href={back} onClick={linkHandler(back)} aria-label="Back" style={{ width: "var(--tap-min)", height: "var(--tap-min)", marginLeft: -10 }}>
          <BackIcon size={22} />
        </a>
        <h1 className="page__title">{page === "privacy" ? "Privacy" : "Terms"}</h1>
      </div>
      {page === "privacy" ? <Privacy /> : <Terms />}
    </div>
  );
}
